import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SideBarMenuService } from './side-bar-menu.service';
import { PermissionService } from './permission.service';

// Catches the Android app being re-opened via the lumberlinq://payment-success deep link that
// PaymentService.navigateToPaymentSuccess redirects the Custom Tab to once checkout completes
// (see AndroidPaymentHandoffGuard for the outbound half of this flow). Deliberately a distinct
// custom-scheme host from lumberlinq://oauth/callback (owned by MobileOAuthCallbackService) —
// reusing the generic https://app.lumberlinq.com/* App Link here would risk Android/Chrome
// intercepting an earlier, unintended in-flow navigation (e.g. the handoff-consumption
// redirect to the plan picker), since that intent-filter has no path restriction.
//
// init() is called once from AppComponent's constructor (same pattern as DeepLinkService/
// MobileOAuthCallbackService there), and is a no-op on web/iOS/non-native.
@Injectable({ providedIn: 'root' })
export class AndroidPaymentReturnService {
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly sideBarMenuService = inject(SideBarMenuService);
  private readonly permissionService = inject(PermissionService);

  private static readonly DEEP_LINK_PREFIX = 'lumberlinq://payment-success';
  private static readonly PENDING_FLAG = 'androidPaymentHandoffPending';

  init(): void {
    if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android')) return;

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.ngZone.run(() => this.handleUrlOpen(event));
    });

    // Safety net — some OEM Custom Tab providers don't hand off custom schemes reliably.
    // If the pending flag is still set on resume, the user is back in the app one way or
    // another (completed payment and the deep link silently failed, or backed out of the tab)
    // — resync menu/permissions either way; harmless if nothing actually changed.
    App.addListener('resume', () => {
      this.ngZone.run(() => this.consumePendingFlag());
    });
  }

  private handleUrlOpen(event: URLOpenListenerEvent): void {
    const raw = event?.url || '';
    if (!raw.startsWith(AndroidPaymentReturnService.DEEP_LINK_PREFIX)) return;

    Browser.close().catch(() => { /* already closed — not fatal */ });
    localStorage.removeItem(AndroidPaymentReturnService.PENDING_FLAG);

    const queryIndex = raw.indexOf('?');
    const query = queryIndex >= 0 ? raw.slice(queryIndex + 1) : '';
    this.logger.info('[AndroidPaymentReturn] Navigating to payment success');
    this.router.navigateByUrl(`/payment/success${query ? '?' + query : ''}`);
  }

  private consumePendingFlag(): void {
    if (!localStorage.getItem(AndroidPaymentReturnService.PENDING_FLAG)) return;
    localStorage.removeItem(AndroidPaymentReturnService.PENDING_FLAG);
    this.logger.info('[AndroidPaymentReturn] Resume safety net — resyncing menu/permissions');
    this.sideBarMenuService.requestMenuRefresh();
    this.permissionService.refreshPermissions().subscribe();
  }
}
